import {Tr} from "@/i18n/i18n";
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import TimeUtils from "@/utils/time-utils";
import PricePanel from "@/scenes.ui/price-panel";
import {SquareButton} from "@/scenes.ui/square-button";
import {Fonts} from "@/constants/font";
import PlayerStore from "@/store/player";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import {BottomForcesPanel} from "@/scenes.ui/bottom-forces-panel";
import {StringUtils} from "@/utils/string-utils";
import {CreatureUtils} from "@/utils/creature-utils";
import {ForceUpdateInfo} from "@/store/force/types";
import {ForceBasePanel} from "@/scenes.ui/force-base-panel";
import {ForceDescription, Lang, PayType} from "@/store/player/types";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import {Link} from "@/scenes.ui/link";
import {Force, WarriorClass} from "@/store/level/types";
import BBCodeText from "phaser3-rex-plugins/plugins/bbcodetext";
import Text from "phaser3-rex-plugins/plugins/gameobjects/text/textbase/Text";
import {CardInfo, CardType, EvolutionInfo} from "@/store/card/types";
import CardStore from "@/store/card";
import {CardPanel} from "@/scenes.ui/card-panel";
import {EvolutionPanel} from "@/scenes.ui/EvolutionPanel";
import {EvolutionGrid} from "@/scenes.ui/EvolutionGrid";

export class UpdateForcePanel extends ForceBasePanel {
    private ui: ForceUpdateInfo;
    private evolutionButton: SquareButton | null = null;
    private creatureId: number;
    private improveButton: SquareButton | null = null;

    constructor(scene: Scene, canvas: HTMLCanvasElement, ui: ForceUpdateInfo, improveCallback: () => unknown, cantUpdateCallback: (alertText: string) => unknown, slotX: number, slotY: number, container: Container, showForceManualCallback: (wc: WarriorClass, rankLimit: number) => void, creatureId: number) {
        super(scene, canvas, slotX, slotY, container);
        this.ui = ui;
        this.creatureId = creatureId;

        let fromZeroRank = ui.currentLevel == 0;
        let blackZone = fromZeroRank ? SpriteUtils.addSprite(this.scene, 0, -150 + 2 + 12, Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_BLACK_ZONE, 0.95, 2.1) : SpriteUtils.addSprite(this.scene, 0, -150 + 2, Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_BLACK_ZONE, 0.95, 1.7);
        let blackZone3 = SpriteUtils.addSprite(this.scene, 0, 82, Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_BLACK_ZONE, 0.95, 3.2);
        blackZone3.setOrigin(0.5, 0);
        let forceName = PlayerStore.getPlayer().lang == Lang.RU ? StringUtils.capitalizeFirstLetter(Tr('force')) + ' ' + StringUtils.capitalizeFirstLetter(CreatureUtils.getClassNameGenetive(ui.warriorClass)) : StringUtils.capitalizeFirstLetter(CreatureUtils.getClassNameGenetive(ui.warriorClass)) + ' ' + StringUtils.capitalizeFirstLetter(Tr('force'));
        const title = TextUtils.addTextByFamily(this.scene, forceName, 0, -278, '#ffffff', 16, 'normal', 420, Fonts.ARIAL_MT).setOrigin(0.5, 0.5);
        if (ui.currentLevel == 0 && ui.nextLevel > 1) {
            forceName += ' ' + ui.nextLevel;
        }
        let updateTextString = fromZeroRank ? Tr('explore.new.force').toUpperCase() + '?' : Tr('uluchshit.do.ranga').toUpperCase() + String(ui.nextLevel) + '?';
        let updateText = TextUtils.addTextByFamily(scene, updateTextString, 0, -223, fromZeroRank ? '#72d91f' : '#ffd465', 18, 'normal', 450, Fonts.MYRIADPRO_BOLD);
        let lightFrom = SpriteUtils.addSprite(this.scene, -87 - 4, -150 + 6, Keys.ATLAS_UI, Keys.UI_BADGE_LINER, 1.1);
        let lightTo = SpriteUtils.addSprite(this.scene, fromZeroRank ? 0 - 4 : 87 - 4, -150 + 6, Keys.ATLAS_UI, Keys.UI_BADGE_LINER, fromZeroRank ? 1.1 : 1.1);
        let itemFrom = SpriteUtils.addSprite(this.scene, -87, -150, CreatureUtils.getForceAtlasLarge(ui.warriorClass), CreatureUtils.getLargeForceIcon(ui.warriorClass, true), 0.8)
        let fromText = TextUtils.addTextAdvanced(scene, Tr('rang') + " " + ui.currentLevel, -87, -87, '#e0e0e0', 13, 'normal', 150, 'center', 2, Fonts.ARIAL_MT);
        let itemArr = SpriteUtils.addSprite(this.scene, 0, -150, Keys.ATLAS_PROFILE, Keys.UI_ART_ARR);
        let itemTo = SpriteUtils.addSprite(this.scene, fromZeroRank ? 0 : 87, -150, CreatureUtils.getForceAtlasLarge(ui.warriorClass), CreatureUtils.getLargeForceIcon(ui.warriorClass, true), 0.8)
        let toText = TextUtils.addTextAdvanced(scene, fromZeroRank ? forceName : Tr('rang') + " " + (ui.nextLevel), fromZeroRank ? 0 : 87, -87, '#85ff85', 13, 'normal', 250, 'center', 2, Fonts.ARIAL_MT);
        const fromRankBg = SpriteUtils.addImage(this.scene, -87 + 33, -150 + 33, Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_INDICATOR_BG, 1.1, 1.1).setOrigin(0.5, 0.5);
        let fromRankNumber = TextUtils.addTextByFamily(scene, ui.currentLevel.toString(), -87 + 34, -150 + 35, '#daba6f', 15, 'bold', 450, Fonts.BENGALY_NORMAL).setOrigin(0.5, 0.5);
        const toRankBg = SpriteUtils.addImage(this.scene, (fromZeroRank ? 0 : 87) + 33, -150 + 33, Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_INDICATOR_BG, 1.1, 1.1).setOrigin(0.5, 0.5);
        let toRankNumber = TextUtils.addTextByFamily(scene, (ui.currentLevel + 1).toString(), (fromZeroRank ? 0 : 87) + 34, -150 + 35, '#85ff85', 15, 'bold', 450, Fonts.BENGALY_NORMAL).setOrigin(0.5, 0.5);
        lightFrom.setVisible(!fromZeroRank);
        itemFrom.setVisible(!fromZeroRank);
        fromText.setVisible(false);
        toText.setVisible(fromZeroRank);
        fromRankBg.setVisible(!fromZeroRank);
        fromRankNumber.setVisible(!fromZeroRank);
        toRankBg.setVisible(!fromZeroRank);
        toRankNumber.setVisible(!fromZeroRank);
        let tagStyle: Text.TextStyle = {
            fontFamily: Fonts.ARIAL_MT,
            fontSize: '13pt',
            align: 'center',
            wrap: {mode: 1, width: 300},
            color: '#d7d7d7'
        }
        const forceInfoLabel = new BBCodeText(this.scene, 0, fromZeroRank ? 4 : -22, this.replaceTags(fromZeroRank ? ui.description : ui.diffDescription), tagStyle).setScale(CU.S, CU.S);
        forceInfoLabel.setOrigin(0.5, 0.5);
        const link: Link = new Link(this.scene, -80 * CU.S, 44, Tr('podrobnee.o.rangah'), () => {
            showForceManualCallback(ui.warriorClass, ui.maxLevel);
        }, '#91918d', 14, 0, Fonts.ARIAL_MT);
        link.setVisible(!fromZeroRank);
        const activeCard = this.getActiveCard();
        const timeText = TextUtils.addTextAdvanced(this.scene, activeCard === CardType.SNAKE ? TimeUtils.getDurationText(ui.updateTimeSec * 0.2) : TimeUtils.getDurationText(ui.updateTimeSec), 10, 110, '#ae9d26', 17, 'normal', 150, 'left', 2, Fonts.MYRIADPRO_BOLD).setOrigin(0.5, 0.5);
        let timeIcon = SpriteUtils.addSprite(this.scene, -timeText.width / 2 - 15, 110, Keys.ATLAS_PROFILE, Keys.UI_ART_TIME_ICON);
        let itemY = 107;
        let canUpdate = true;
        let optionalAlert = '';
        let monsterLevel = ui.minMonsterLevel || 0;
        let player = PlayerStore.getPlayer();
        let ri: RequiredItem = new RequiredItem(scene, canvas, -15, itemY * CU.S, String(monsterLevel) + ' ' + Tr('y.uroven.monstra'), player.bestVictoryStair >= monsterLevel);
        canUpdate = canUpdate && player.bestVictoryStair >= monsterLevel;
        if (player.bestVictoryStair < monsterLevel) {
            optionalAlert = Tr('dlja.uluchshenija.neobhodima.pobeda') + String(monsterLevel) + '!';
        }
        ri.setVisible(!canUpdate);

        if (ui.nsRequired > 0) {
            canUpdate = false;
            optionalAlert = Tr(ui.nsRequired === 1 ? 'need.ns' : 'need.ns.count').replace('{count}', String(ui.nsRequired));
            let nsItem = new RequiredItem(scene, canvas, -10, itemY * CU.S, optionalAlert, false);
            this.add(nsItem);
        }
        if (ui.arkRequired > 0) {
            canUpdate = false;
            let arkItem = new RequiredItem(scene, canvas, -10, itemY * CU.S, Tr('need.ark'), false);
            this.add(arkItem);
        }

        timeText.setVisible(canUpdate);
        timeIcon.setVisible(canUpdate);
        let oldPrice = ui.amount.mithril;
        let price = PlayerStore.GET_CARDS_STATE()!.activeCard === CardType.ELEPHANT ? 0 : PlayerStore.GET_CARDS_STATE()!.activeCard === CardType.SNAKE ? ui.amount.mithril * 0.2 : ui.amount.mithril;
        let haveDiscount = price != oldPrice;
        if (price > player.mithril) {
            canUpdate = false;
            optionalAlert = Tr('ne.hvataet.izumrudov');
        }
        this.improveButton = new SquareButton(this.scene, 0, 192, -15 * CU.S, canUpdate ? Keys.UI_ART_GREEN_BUTTON : Keys.UI_ART_RED_BUTTON, haveDiscount ? undefined : Keys.ATLAS_PROFILE, haveDiscount ? Keys.IC_100_MITHRIL : Keys.UI_ART_UPPER_ICON, fromZeroRank ? Tr('explore') : Tr('uluchshit'), false, 90 * CU.S, 90 * CU.S);
        this.improveButton.setScale(0.9, 0.9);
        this.improveButton.setCallback(() => {
            if (this.isOnTop()) {
                const ongoingUpgrades = PlayerStore.getPlayer().creatures.flatMap(creature => creature.forceUpgrades.filter(fu => fu.updatingTill && fu.updatingTill > TimeUtils.getServerTimeMs()));
                if (canUpdate && ongoingUpgrades.length >= PlayerStore.GET_HAMMERS_COUNT()) {
                    cantUpdateCallback(Tr('vse.molotki.zaniati'));
                } else if (canUpdate) {
                    improveCallback();
                } else {
                    cantUpdateCallback(optionalAlert);
                }
            }
        });
        this.add([blackZone, /*blackZone2,*/ blackZone3, title, updateText, lightFrom, lightTo, itemFrom, itemArr, itemTo, fromText, toText, timeIcon, timeText, this.improveButton, ri, forceInfoLabel, fromRankBg, fromRankNumber, toRankBg, toRankNumber, link]);
        const showTwoButtons = this.isTwoButtonMode(activeCard);

        if (showTwoButtons) {
            if (activeCard) {
                this.updateEvolutionButton();
            }
            const buttonsContainer = new Phaser.GameObjects.Container(this.scene, 0, 180);
            const spacing = 130 * CU.S;
            this.improveButton.x = -spacing / 2;
            if (this.evolutionButton) {
                this.evolutionButton.x = spacing / 2;
                buttonsContainer.add(this.evolutionButton);
            }
            buttonsContainer.add(this.improveButton);
            this.add(buttonsContainer);
            {
                const localY = 35;
                const priceText = !haveDiscount ? TextUtils.addText(this.scene, price.toString(), 0, localY, '#ffffff', 18, 'bold', 200) : TextUtils.addTextAdvanced(this.scene, price.toString(), 0, localY, '#0cff00', 19, 'normal', 200, 'center', 2, Fonts.MYRIADPRO_BOLD);
                if (haveDiscount) {
                    priceText.setShadow(0, 0, '#ffffff');
                    priceText.setStroke('#000000', 2);
                }
                priceText.setOrigin(0.5, 0.5);
                this.improveButton.add(priceText);
                const iconX = -(priceText.width / 2) - 20;
                const priceIcon = SpriteUtils.addSprite(this.scene, iconX, localY, haveDiscount ? '' : Keys.ATLAS_PROFILE, haveDiscount ? Keys.IC_100_MITHRIL : Keys.UI_ART_UPPER_ICON, 1);
                priceIcon.setOrigin(0.5, 0.5);
                if (haveDiscount) {
                    const oldPriceText = TextUtils.addTextAdvanced(this.scene, TextUtils.formatGems(oldPrice), 0, localY - 20, '#ffffff', 13, 'normal', 200, 'center', 2, Fonts.MYRIADPRO_REGULAR);
                    oldPriceText.setShadow(0, 0, '#ffffff');
                    oldPriceText.setStroke('#000000', 2);
                    oldPriceText.setOrigin(0.5, 0.5);
                    this.improveButton.add(oldPriceText);
                    const underline = this.scene.add.line(0, 0, oldPriceText.getLeftCenter().x, oldPriceText.getLeftCenter().y, oldPriceText.getRightCenter().x, oldPriceText.getRightCenter().y, 0xFF0000, 1);
                    this.improveButton.add(underline);
                }
            }
            this.add(this.improveButton);
            if (this.evolutionButton) this.add(this.evolutionButton);
        } else {
            if (haveDiscount) {
                this.addPrice(this.scene, canvas, 0, this.improveButton.y / CU.S + 35 + 5, oldPrice, price);
            } else {
                const pricePanel: PricePanel = new PricePanel(this.scene, 44 * CU.S, this.improveButton.y + 50 * CU.S, ui.amount);
                pricePanel.setOrigin(0.5, 0.5);
                this.add(pricePanel);
            }
        }
    }

    private addPrice(scene: Scene, canvas: HTMLCanvasElement, x: number, y: number, oldPrice: number, price: number) {
        let buyPriceText;
        let buyTextY = y;
        if (oldPrice === price) {
            buyPriceText = TextUtils.addText(scene, price.toString(), x, buyTextY, '#ffffff', 18, 'bold', 200);
        } else {
            buyPriceText = TextUtils.addTextAdvanced(scene, price.toString(), x, buyTextY, '#0cff00', 19, 'normal', 200, 'center', 2, Fonts.MYRIADPRO_BOLD);
            buyPriceText.setShadow(0, 0, '#ffffff');
            buyPriceText.setStroke('#000000', 2);
        }
        this.add(buyPriceText);
        if (oldPrice !== price) {
            let oldPriceText = TextUtils.addTextAdvanced(scene, TextUtils.formatGems(oldPrice), x, y - 20, '#ffffff', 13, 'normal', 200, 'center', 2, Fonts.MYRIADPRO_REGULAR);
            oldPriceText.setShadow(0, 0, '#ffffff');
            oldPriceText.setStroke('#000000', 2);
            let underline = scene.add.line(0, 0, oldPriceText.getLeftCenter().x! + 18 * CU.S, oldPriceText.getLeftCenter().y!, oldPriceText.getRightCenter().x! + 14 * CU.S, oldPriceText.getRightCenter().y! + 14 * CU.S, 0xFF0000, 1);
            this.add([oldPriceText, underline]);
        }
    }

    public open() {
        super.open();
        if (BottomForcesPanel.instance) {
            BottomForcesPanel.instance.visible = false;
        }
        const activeCard = this.getActiveCard();
        if (!this.isTwoButtonMode(activeCard)) return;
        this.updateEvolutionButton();
    }

    public close() {
        super.close();
        if (BottomForcesPanel.instance) {
            BottomForcesPanel.instance.visible = true;
        }
    }

    private replaceTags(str: string) {
        let fromColor = '#e3ce95';
        let toColor = '#85ff85';
        str = str.replaceAll('[w]', '[color=' + fromColor + ']');
        str = str.replaceAll('[g]', '[color=' + toColor + ']');
        str = str.replaceAll('[/w]', '[/color]');
        str = str.replaceAll('[/g]', '[/color]');
        return str;
    }

    private getTargetClass(): WarriorClass {
        return this.ui.warriorClass;
    }

    private getActiveCard(): CardType | null {
        const cardState = PlayerStore.GET_CARDS_STATE();
        return cardState ? cardState.activeCard : null;
    }

    private isTwoButtonMode(activeCard: CardType | null): boolean {
        return !!activeCard && activeCard !== CardType.ELEPHANT && activeCard !== CardType.SNAKE && activeCard !== CardType.EAGLE;
    }

    private getCreatureId(): number {
        return this.creatureId;
    }

    private createEvolutionButton(activeCard: CardType): SquareButton {
        const cardIconKey = CardPanel.getCardSmallImage(activeCard);
        const btn = new SquareButton(this.scene, 60, 192, CU.S, Keys.UI_ART_GREEN_BUTTON, undefined, cardIconKey, Tr('evolution'), false, 90 * CU.S, 110 * CU.S);
        btn.setScale(0.9, 0.9);
        btn.setVisible(false);
        this.add(btn);
        return btn;
    }

    private updateEvolutionButton(): void {
        const activeCard = PlayerStore.GET_CARDS_STATE()?.activeCard;
        if (!activeCard){
            return;
        }
        if (!this.evolutionButton) {
            this.evolutionButton = this.createEvolutionButton(activeCard);
        }
        const creatureId = this.getCreatureId();
        const currentForce = this.getTargetClass();

        const incompatibleError = UpdateForcePanel.isCardIncompatible(activeCard, creatureId);
        if (incompatibleError) {
            if (this.improveButton) {
                this.improveButton.x = 0;
                const warningY = this.improveButton.displayHeight / CU.S + 240;
                const warningLabel = TextUtils.addTextByFamily(this.scene, incompatibleError, 0, warningY, "#ff4c4c", 13, "bold", 350, Fonts.ARIAL_MT).setOrigin(0.5, 0);
                this.improveButton.y -= 10;
                this.improveButton.setScale(0.7);
                this.add(warningLabel);
            }
            this.evolutionButton?.setVisible(false);
            return;
        }

        if (activeCard !== CardType.EVOLUTION_UNIVERSAL) {
            const targetWarriorClass = EvolutionPanel.getWarriorClassByCardType(activeCard);
            if (!targetWarriorClass) {
                return;
            }
            CardStore.checkEvolutionAvailability(creatureId, currentForce, targetWarriorClass).then((canEvolve) => {
                if (canEvolve) {
                    this.evolutionButton?.setVisible(true);
                    this.evolutionButton?.setCallback(() => {
                        new EvolutionPanel(this.scene, this.scene.sys.canvas, currentForce, targetWarriorClass, () => {
                            this.setVisible(true);
                        }, () => {
                        }, this.x, this.y, this.container ?? new Container(this.scene), creatureId).open();
                        this.close();
                    });
                } else {
                    if (this.improveButton) {
                        this.improveButton.x = 0;
                    }
                    const incompatibleError = UpdateForcePanel.isCardIncompatible(activeCard, creatureId);
                    if (incompatibleError) {
                        if (this.improveButton) {
                            this.improveButton.x = 0;
                            const warningY = this.improveButton.displayHeight / CU.S + 240;
                            const warningLabel = TextUtils.addTextByFamily(this.scene, incompatibleError, 0, warningY, "#ff4c4c", 13, "bold", 350, Fonts.ARIAL_MT).setOrigin(0.5, 0);
                            this.improveButton.y -= 10;
                            this.improveButton.setScale(0.7);
                            this.add(warningLabel);
                        }
                        this.evolutionButton?.setVisible(false);
                        return;
                    }
                    this.evolutionButton?.setVisible(false);
                }
            }).catch((error) => {
                console.log(error);
            });
        } else {
            const creature = CreatureUtils.getCreatureById(this.getCreatureId());
            const hasSelectedForce = CreatureUtils.hasForce(creature, this.getTargetClass());
            if (!hasSelectedForce) {
                if (this.improveButton) {
                    this.improveButton.x = 0;
                }
                this.evolutionButton?.setVisible(false);
                return;
            }
            this.evolutionButton?.setVisible(true);
            this.evolutionButton?.setCallback(() => {
                const creatureId = this.getCreatureId();
                const currentForce = this.getTargetClass();
                const heroName = CreatureUtils.getCreatureById(creatureId)?.name || "hero";
                CardStore.fetchEvolutionOptionsByCreature(creatureId, currentForce).then((evolutionInfo: EvolutionInfo) => {
                    if (!evolutionInfo || !evolutionInfo.availableClasses || evolutionInfo.availableClasses.length === 0) {
                        return;
                    }
                    new EvolutionGrid(this.scene, this.scene.sys.canvas, evolutionInfo, (selectedForce: WarriorClass) => {
                        new EvolutionPanel(this.scene, this.scene.sys.canvas, currentForce, selectedForce, () => {
                            this.setVisible(true);
                        }, () => {
                        }, this.x, this.y, this.container ?? new Container(this.scene), creatureId).open();
                        this.close();
                    }, this.getTargetClass(), heroName).open();
                }).catch((error) => {
                    console.log(error);
                });
            });
        }
    }

    static isCardIncompatible(card: CardType, creatureId: number): string | null {
        const cardToGiftMap: Partial<Record<CardType, WarriorClass>> = {
            [CardType.EVOLUTION_PHOENIX]: WarriorClass.ASG_PHOENIX,
            [CardType.EVOLUTION_SHADOW]: WarriorClass.LIGHTSHADOW,
            [CardType.EVOLUTION_INKUB]: WarriorClass.SUKKUB,
            [CardType.EVOLUTION_SUKKUB]: WarriorClass.INKUB,
        };

        const requiredGift = cardToGiftMap[card];
        if (!requiredGift) return null;

        const cr = CreatureUtils.getCreatureById(creatureId);
        if (!cr) return null;

        const alreadyHasForbiddenGift = cr.forcesV2?.some(f => f.warriorClass === requiredGift);
        if (alreadyHasForbiddenGift) {
            let errorMessage = Tr("incompatible.force");
            switch (card) {
                case CardType.EVOLUTION_PHOENIX:
                    errorMessage += Tr("incompatible.force.phoenix");
                    break;
                case CardType.EVOLUTION_SHADOW:
                    errorMessage += Tr("incompatible.force.shadow");
                    break;
                case CardType.EVOLUTION_INKUB:
                    errorMessage += Tr("incompatible.force.inkub");
                    break;
                case CardType.EVOLUTION_SUKKUB:
                    errorMessage += Tr("incompatible.force.sukkub");
                    break;
            }
            return errorMessage;
        }

        return null;
    }
}

class RequiredItem extends Phaser.GameObjects.Container {
    constructor(scene: Scene, canvas: HTMLCanvasElement, x: number, y: number, name: string, existing: boolean) {
        super(scene);
        this.x = x;
        this.y = y;
        let valueText = TextUtils.addTextAdvancedNoWidth(scene, name, 0, 0, '#ae9d26', 16, 'normal', 400, 'center', 2, Fonts.ARIAL_MT);
        let existIcon = SpriteUtils.addSprite(this.scene, valueText.getRightCenter().x! / CU.S + 26, 0, Keys.ATLAS_PROFILE, RequiredItem.getExistanceSign(existing));
        SpriteUtils.setDispSize(existIcon, 32 * CU.S, 32 * CU.S);
        this.add([valueText, existIcon]);
    }

    private static getExistanceSign(existing: boolean): string {
        return existing ? Keys.UI_CHECK_SIGN : Keys.UI_CROSS_SIGN;
    }
}